import { useState, useMemo } from "react";
import { useOktaAuth } from "@okta/okta-react";
import { GetDataWithCleanedDates } from "shared/helpers/DateHelpers";
import axios from "axios";
import { GetCustomError } from "shared/helpers/AxiosHelpers";
import { IsNullOrEmpty } from "shared/helpers/NullEmptyHelpers";

import { useQuery } from "@tanstack/react-query";

export function useExportServerGridData(
  apiGetEndpoint,
  apiSubmittedFormData,
  serverGridDataState,
  onFailureCallback,
  apiPostEndpoint = null
) {
  const { oktaAuth } = useOktaAuth();

  const [runningExportData, setRunningExportData] = useState(false);
  const [exportColumns, setExportColumns] = useState([]);

  const [exportFormat, setExportFormat] = useState();

  //This is the largest number of records that can be exported to Excel
  const maxExportRecordsCount = 1048576;

  const dataKey = "useExportServerGridData";

  function ExportHasCompleted() {
    setRunningExportData(false);
  }

  const apiParameters = useMemo(() => {
    if (!serverGridDataState) return {};
    if (!apiSubmittedFormData) return {};

    // Always start with serverGridDataState which contains skip, take, sort, filter
    let parameters = { ...serverGridDataState };

    // Handle different parameter structures for GET vs POST endpoints
    const usePostEndpoint = apiPostEndpoint && apiPostEndpoint.trim() !== "";
    
    if (usePostEndpoint) {
      // For POST endpoints, preserve the structured format (e.g., Report object)
      // while ensuring grid state parameters (skip, take, sort, filter) are at root level
      parameters = { ...parameters, ...apiSubmittedFormData };
    } else {
      // For GET endpoints, merge all properties at the root level (original behavior)
      for (const [key, value] of Object.entries(apiSubmittedFormData)) {
        parameters[key] = value;
      }
    }

    return parameters;
  }, [serverGridDataState, apiSubmittedFormData, apiPostEndpoint]);

  //this will update the api params to get the data for the export, it will set the take to the maxExportRecordsCount
  function getExportDataParameters() {
    //Set the parameters to the serverGridDataState
    //make a copy as we do not want to change the original
    let exportDataParameters = { ...apiParameters };

    //invalidated this will move exports to a hook
    //let exportDataParameters = {};

    //find a property with Key of "take" and set it to the maxExportRecordsCount
    for (const [key] of Object.entries(exportDataParameters)) {
      if (key === "take") {
        exportDataParameters[key] = maxExportRecordsCount;
      }
    }

    //find a property with Key of "skip and set it to 0
    for (const [key] of Object.entries(exportDataParameters)) {
      if (key === "skip") {
        exportDataParameters[key] = 0;
      }
    }

    return exportDataParameters;
  }

  function InitiateExport(format, columns) {
    setRunningExportData(true);

    setExportColumns(columns);
    setExportFormat(format);
    //getFullExportServerGridData();
  }

  function ProcessedServerGridData(response) {
    let data = [];

    //if the response comes from GridDataProcessor, the data will be in a sub property called gridData, and also Total, and FieldTotals
    // FYI: Here is the model from GridDataProcessor:  { GridData = query.ToList(), Total = total, FieldTotals = calculatedTotals }
    //Otherwise the data will be the response.data and we need to distinguish between the two
    if (response.data.gridData) {
      data = response.data.gridData;
    } else if (response.data) {
      data = response.data;
    }

    let dataWithCleanedDates = GetDataWithCleanedDates(data);

    console.log(response.data);

    if (response.data.total) {
      dataWithCleanedDates.total = response.data.total;
    }

    //set totals if provided
    if (response.data.fieldTotals) {
      dataWithCleanedDates.fieldTotals = response.data.fieldTotals;
    }
    //call setServerGridParentData if it is provided
    //if (setServerGridParentData) setServerGridParentData(dataWithCleanedDates);

    return dataWithCleanedDates;
  }

  //This function will get the data from the server
  async function GetServerGridExportData() {
    //if api paramss are null or empty return empty array, no need to make a call
    if (!apiParameters || Object.keys(apiParameters).length === 0) {
      return [];
    }

    let accessToken = oktaAuth.getAccessToken();

    let exportParameters = getExportDataParameters();

    // Determine which endpoint and method to use
    const usePostEndpoint = apiPostEndpoint && apiPostEndpoint.trim() !== "";
    const endpoint = usePostEndpoint ? apiPostEndpoint : apiGetEndpoint;

    var result = await axios({
      method: usePostEndpoint ? 'POST' : 'GET',
      url: endpoint,
      ...(usePostEndpoint 
        ? { data: exportParameters } 
        : { params: exportParameters }
      ),
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then((response) => {
        let cleanedData = ProcessedServerGridData(response);

        return cleanedData;
      })
      .catch((err) => {
        // console.error(err);

        if (onFailureCallback) onFailureCallback(err);

        let thisError = GetCustomError(err);

        throw thisError;
      });

    return result;
  }

  function IsEnabled() {
    if (!runningExportData) return false;

    // Check if either GET or POST endpoint is provided
    const hasValidEndpoint = apiGetEndpoint || (apiPostEndpoint && apiPostEndpoint.trim() !== "");
    if (!hasValidEndpoint) return false;

    if (IsNullOrEmpty(apiParameters)) return false;

    return true;
  }

  const { data: fullExportData } = useQuery({
    queryKey: [dataKey, apiGetEndpoint, apiPostEndpoint, apiParameters],
    queryFn: async () => await GetServerGridExportData(),
    enabled: IsEnabled,
  });

  //will return the data to the parent
  return {
    runningExportData,
    exportColumns,
    setExportColumns,
    exportFormat,
    setExportFormat,
    fullExportData,
    InitiateExport,
    ExportHasCompleted,
  };
}
